import React, { FC } from 'react';
import { useFormikContext } from 'formik';
import { PaymentDetailsType } from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { usePurchaseContext } from 'QuoteApp/services/providers';
import { useAppContext } from 'QuoteApp/services/providers';
import { PurchaseFormValuesType } from 'QuoteApp/screens/Purchase/types';
import { ContactInfoV2 } from './ContactInfo';
import { PaymentDisclaimer } from './PaymentDisclaimer';
import { PaymentFrequencies } from './PaymentFrequencies';
import { PaymentMethods } from './PaymentMethods';
import { OchoPaymentMethod } from './OchoPaymentMethod';
import { SweepPaymentInfo } from './SweepPaymentInfo';
import { SweepPaymentSetup } from './SweepPaymentSetup';
import styles from './styles.module.css';

export const MainContent: FC<{
  paymentDetails: PaymentDetailsType;
}> = ({ paymentDetails }) => {
  const {
    paymentMethodsSectionRef,
    setIsEditingContactInfo,
    eSignatureErrors,
    setESignatureErrors,
  } = usePurchaseContext();

  const { config } = useAppContext();
  const { values, setFieldValue } = useFormikContext<PurchaseFormValuesType>();

  const isSweepPurchase = !!config?.sweepEnabled;

  const isPaymentFlexibilityEnabled = config?.flexiblePayments.enabled;

  const renderPaymentFrequencies = () => {
    return (
      <div className={styles.mainSection}>
        <h3>Payment frequency</h3>
        <PaymentFrequencies paymentDetails={paymentDetails} />
      </div>
    );
  };

  return (
    <div className={styles.mainWrapper}>
      {!isPaymentFlexibilityEnabled && renderPaymentFrequencies()}
      {isSweepPurchase && (
        <div
          className={`${styles.mainSection} ${styles.sweepSection}`}
          data-testid="sweep-payment-info-section"
        >
          <SweepPaymentInfo />
        </div>
      )}
      {paymentDetails.paymentProvider === 'adyen' &&
        (isSweepPurchase ? (
          <div
            className={styles.mainSection}
            ref={paymentMethodsSectionRef}
            data-testid="sweep-payment-setup-section"
          >
            <SweepPaymentSetup
              autopayEnabled={values.autopayEnabled}
              sweepAffirmationAccepted={values.sweepAffirmationAccepted}
              onAutopayChange={(value: boolean) =>
                setFieldValue('autopayEnabled', value)
              }
              onAffirmationChange={(value: boolean) => {
                setFieldValue('sweepAffirmationAccepted', value);
                if (value && eSignatureErrors?.sweepAffirmation) {
                  const { sweepAffirmation, ...rest } = eSignatureErrors;
                  setESignatureErrors(
                    Object.keys(rest).length > 0 ? rest : null
                  );
                }
              }}
              affirmationError={eSignatureErrors?.sweepAffirmation}
            >
              <PaymentMethods paymentDetails={paymentDetails} embedded />
            </SweepPaymentSetup>
          </div>
        ) : (
          <div
            className={styles.mainSection}
            ref={paymentMethodsSectionRef}
            data-testid="payment-methods"
          >
            <h3>Payment method</h3>
            <PaymentDisclaimer variant="warning" />
            <PaymentMethods paymentDetails={paymentDetails} />
          </div>
        ))}
      {paymentDetails.paymentProvider === 'agentPay' && (
        <div className={styles.mainSection}>
          <h3>Payment method</h3>
          <OchoPaymentMethod />
        </div>
      )}
      <div className={styles.mainSection}>
        <ContactInfoV2 setIsEditing={setIsEditingContactInfo} />
      </div>
      {paymentDetails.paymentProvider === 'processOne' && (
        <div className={styles.mainSection}>
          <h3>Payment method will be collected next</h3>
          <PaymentDisclaimer variant="light-brand" />
        </div>
      )}
    </div>
  );
};
